import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Loader2, RefreshCw, ShieldCheck, ShieldAlert, X, Filter, CheckCircle2, Coins, PenLine, Download } from "lucide-react";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const FILTERS = [
  ["all",                  "All"],
  ["pending_confirmation", "Pending confirm"],
  ["active",               "Active"],
  ["expiring_soon",        "Expiring"],
  ["delivered",            "Delivered"],
  ["reconciled",           "Reconciled"],
  ["paid",                 "Paid"],
  ["expired",              "Expired"],
];

const STATUS_TONE = {
  pending_confirmation: "text-[#D8B23E] border-[#D8B23E]/40",
  active:               "text-[#3DBB84] border-[#3DBB84]/40",
  expiring_soon:        "text-[#E5484D] border-[#E5484D]/40",
  delivered:            "text-[#2FE4C6] border-[#2FE4C6]/40",
  reconciled:           "text-[#F5F5F0] border-[#F5F5F0]/40",
  paid:                 "text-[#D8B23E] border-[#D8B23E]/40",
  expired:              "text-[#8A8A82] border-[#8A8A82]/40",
  cancelled:            "text-[#8A8A82] border-[#8A8A82]/40",
};

export function AdminDealProtection({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/deals");
      setRows(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load deals"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter(r => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    rows.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div data-testid="admin-deal-protection" className="space-y-6">
      <div className="flex items-start gap-4 border-b border-white/10 pb-6">
        <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
          <Shield size={20}/>
        </div>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Deal Protection Desk · Admin</div>
          <h2 className="font-serif text-2xl text-[#F5F5F0] mt-1">Indirect trade registry · every deal, every clock.</h2>
          <p className="font-body text-xs text-[#8A8A82] mt-1">
            Confirm suppliers, review timelines, reconcile auto-generated commission claims and release payouts.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="text-[#8A8A82] hover:text-[#F5F5F0] p-1" data-testid="admin-deals-refresh">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] inline-flex items-center gap-1 pr-2">
          <Filter size={11}/> Filter
        </span>
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`deal-filter-${k}`}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm border ${
                    filter === k ? "bg-[#D8B23E] text-black border-[#D8B23E]" : "border-white/15 text-[#8A8A82] hover:text-[#F5F5F0]"
                  }`}>
            {label} <span className="opacity-70">· {counts[k] ?? 0}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-8 text-center font-body text-sm text-[#8A8A82]">
          {loading ? "Loading…" : "No deals in this state."}
        </div>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/40">
              <tr>
                {["Deal Ref", "Partner", "Buyer", "Materials", "Est. Value", "Status", "Days Left"].map(h => (
                  <th key={h} className="text-left font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] px-3 py-2 border-b border-white/10">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id} data-testid={`admin-deal-row-${d.id}`}
                    onClick={() => setActive(d)}
                    className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer">
                  <td className="px-3 py-3 font-mono text-[12px] text-[#F5F5F0]">{d.deal_ref}</td>
                  <td className="px-3 py-3 font-body text-[12px] text-[#C9C9C2]">
                    {d.partner_business || d.partner_email}
                    <div className="font-body text-[10px] text-[#8A8A82]">{d.partner_category}</div>
                  </td>
                  <td className="px-3 py-3 font-body text-[12px] text-[#F5F5F0]">
                    {d.buyer_name}<div className="font-body text-[10px] text-[#8A8A82]">{d.buyer_email}</div>
                  </td>
                  <td className="px-3 py-3 font-body text-[12px] text-[#8A8A82] capitalize">
                    {(d.material_scope||[]).join(", ").replace(/_/g," ")}
                  </td>
                  <td className="px-3 py-3 font-body text-[12px] text-[#D8B23E]">{inr(d.est_value)}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-grotesk text-[9px] uppercase tracking-[0.18em] ${STATUS_TONE[d.status] || "text-[#8A8A82] border-white/20"}`}>
                      {d.status.replace(/_/g," ")}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-body text-[11px] text-[#8A8A82]">
                    {d.protection_days_left === null ? "—" : `${d.protection_days_left}d`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {active && <AdminDealDrawer api={api} deal={active}
                                    onClose={() => setActive(null)}
                                    onUpdated={() => { load(); setActive(null); }}/>}
      </AnimatePresence>
    </div>
  );
}


function AdminDealDrawer({ api, deal, onClose, onUpdated }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [supplierName, setSupplierName] = useState("");
  const [confirmRemarks, setConfirmRemarks] = useState("");
  const [reconcileAmount, setReconcileAmount] = useState("");
  const [reconcileRemarks, setReconcileRemarks] = useState("");

  const loadTl = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/deals/${deal.id}/timeline`);
      setTimeline(r.data || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [api, deal.id]);

  useEffect(() => { loadTl(); }, [loadTl]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/deals/${deal.id}/supplier-confirm`, {
        supplier_name: supplierName.trim(), remarks: confirmRemarks.trim(),
      });
      toast.success("Supplier confirmed · 180-day clock armed");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const reconcile = async () => {
    setBusy(true);
    try {
      const payload = { remarks: reconcileRemarks.trim() };
      if (reconcileAmount) payload.commission_amount = Number(reconcileAmount);
      await api.post(`/admin/deals/${deal.id}/reconcile`, payload);
      toast.success("Reconciled · payout awaiting mark-paid");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/deals/${deal.id}/mark-paid`);
      toast.success("Payout released");
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const download = async (t) => {
    try {
      const r = await api.get(`/deals/${deal.id}/timeline/${t.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not download"); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-stretch justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }}
        className="bg-[#050506] w-full max-w-2xl border-l border-[#D8B23E]/30 h-full overflow-y-auto"
        data-testid="admin-deal-drawer">
        <div className="sticky top-0 z-10 bg-[#050506]/95 backdrop-blur flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Review Deal</div>
            <div className="font-serif text-xl text-[#F5F5F0] font-mono">{deal.deal_ref}</div>
            <div className="font-body text-[11px] text-[#8A8A82]">
              {deal.partner_business} · {deal.buyer_name} · {deal.project}
            </div>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]" data-testid="admin-drawer-close"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Buyer email", deal.buyer_email],
              ["Buyer phone", deal.buyer_phone],
              ["Site address", deal.site_address],
              ["Materials", (deal.material_scope||[]).join(", ").replace(/_/g," ")],
              ["Est. value", inr(deal.est_value)],
              ["Protection days left", deal.protection_days_left === null ? "—" : `${deal.protection_days_left} of ${deal.protection_days}`],
              ["NCND provider", deal.ncnd_provider || "manual"],
              ["NCND status", deal.ncnd_status],
            ].map(([k,v]) => (
              <div key={k} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-3">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{k}</div>
                <div className="font-body text-sm text-[#F5F5F0] mt-1 capitalize break-words">{v || "—"}</div>
              </div>
            ))}
          </div>

          {/* Supplier confirm */}
          {!deal.supplier_confirmed && (
            <div className="border border-[#D8B23E]/40 bg-[#D8B23E]/[0.05] rounded-sm p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Supplier confirmation</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="confirm-supplier" value={supplierName}
                       onChange={e => setSupplierName(e.target.value)}
                       placeholder="Supplier name (optional)"
                       className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
                <input data-testid="confirm-remarks" value={confirmRemarks}
                       onChange={e => setConfirmRemarks(e.target.value)}
                       placeholder="Remarks"
                       className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
              </div>
              <button onClick={confirm} disabled={busy} data-testid="confirm-btn"
                      className="mt-3 inline-flex items-center gap-2 bg-[#3DBB84] hover:bg-[#2fa16e] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <ShieldCheck size={12}/>} Confirm supplier · arm clock
              </button>
            </div>
          )}

          {/* Reconcile */}
          {deal.status === "delivered" && (
            <div className="border border-[#2FE4C6]/40 bg-[#2FE4C6]/[0.05] rounded-sm p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#2FE4C6] mb-2">Reconcile commission</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input data-testid="reconcile-amount" type="number" value={reconcileAmount}
                       onChange={e => setReconcileAmount(e.target.value)}
                       placeholder="Override amount (blank = auto)"
                       className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#2FE4C6]"/>
                <input data-testid="reconcile-remarks" value={reconcileRemarks}
                       onChange={e => setReconcileRemarks(e.target.value)}
                       placeholder="Remarks"
                       className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#2FE4C6]"/>
              </div>
              <button onClick={reconcile} disabled={busy} data-testid="reconcile-btn"
                      className="mt-3 inline-flex items-center gap-2 bg-[#2FE4C6] hover:bg-[#28c5ab] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <Coins size={12}/>} Approve & reconcile
              </button>
            </div>
          )}

          {/* Mark paid */}
          {deal.status === "reconciled" && (
            <div className="border border-[#D8B23E]/40 bg-[#D8B23E]/[0.05] rounded-sm p-4">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Release payout</div>
              <div className="font-body text-[11px] text-[#8A8A82] mb-2">
                Reconciled amount: <strong className="text-[#F5F5F0]">{inr(deal.reconciled_amount)}</strong>
              </div>
              <button onClick={markPaid} disabled={busy} data-testid="mark-paid-btn"
                      className="inline-flex items-center gap-2 bg-[#D8B23E] hover:bg-[#c5a233] disabled:opacity-40 text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>} Mark commission paid
              </button>
            </div>
          )}

          {/* Timeline */}
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mb-2">Proof timeline</div>
            {loading ? (
              <div className="font-body text-sm text-[#8A8A82]">Loading…</div>
            ) : timeline.length === 0 ? (
              <div className="font-body text-sm text-[#8A8A82]">No proofs archived yet.</div>
            ) : (
              <div className="space-y-2" data-testid="admin-timeline">
                {timeline.map(t => (
                  <div key={t.id} className="border border-white/10 rounded-sm bg-black/30 p-3 flex items-start justify-between gap-4">
                    <div>
                      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E]">{t.kind.replace(/_/g," ")}</div>
                      <div className="font-body text-sm text-[#F5F5F0] mt-0.5">{t.note || "—"}</div>
                      <div className="font-body text-[10px] text-[#8A8A82] mt-1">
                        {t.by_email} · {t.filename} · {new Date(t.at).toLocaleString("en-IN", {day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit"})}
                      </div>
                    </div>
                    <button onClick={() => download(t)}
                            className="text-[#D8B23E] hover:text-[#c5a233] shrink-0"><Download size={14}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
